import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDomainCertificates } from '@/hooks/use-domain-certificates';
import { HoldButton } from '@/components/ui/HoldButton';
import { frameCopy } from '@/components/layout/frame-copy';
import { needsRebind, rebindCommand } from '@/lib/rebind';
import type { DomainCertificate } from '@/api/types';

type SignoffValue = {
  waiting: DomainCertificate[];
  openSheet: (id?: string) => void;
};

const SignoffContext = createContext<SignoffValue | null>(null);

const signoffFallback: SignoffValue = { waiting: [], openSheet: () => {} };

export function useSignoff(): SignoffValue {
  return useContext(SignoffContext) ?? signoffFallback;
}

export function SignoffProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const copy = frameCopy(i18n.language);
  const certs = useDomainCertificates({ page: 1, pageSize: 100 });
  const pending = useMemo(
    () => (certs.data?.data ?? []).filter(needsRebind),
    [certs.data],
  );
  const [signed, setSigned] = useState<Record<string, true>>({});
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [fallback, setFallback] = useState('');
  const waiting = pending.filter((cert) => !signed[cert.id]);
  const current = waiting.find((cert) => cert.id === targetId) ?? waiting[0] ?? null;

  const openSheet = (id?: string) => {
    setTargetId(id ?? null);
    setFallback('');
    setOpen(true);
  };

  const finish = async (cert: DomainCertificate) => {
    const cmd = rebindCommand(cert);
    if (!cmd) return;
    try {
      await navigator.clipboard.writeText(cmd);
      setSigned((prev) => ({ ...prev, [cert.id]: true }));
      setOpen(false);
      setFallback('');
      toast.success(copy.signed);
    } catch {
      setFallback(cmd);
    }
  };

  return (
    <SignoffContext.Provider value={{ waiting, openSheet }}>
      {children}
      {open && current ? (
        <div className="np-scrim" role="presentation" onMouseDown={() => setOpen(false)}>
          <div
            className="np-sheet np-crop np-crop-signal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="np-rebind-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <p className="np-tag">{copy.rebind}</p>
            <h2 id="np-rebind-title">{current.primaryDomain}</h2>
            <p className="np-rule">{copy.rebindRule}</p>
            {fallback ? (
              <textarea
                className="np-fallback"
                readOnly
                value={fallback}
                onFocus={(event) => event.currentTarget.select()}
              />
            ) : null}
            <div className="np-sheet-actions">
              <span id="np-hold-hint" className="meta">{copy.holdHint}</span>
              <button type="button" className="np-btn" autoFocus onClick={() => setOpen(false)}>
                {copy.cancel}
              </button>
              <HoldButton
                label={copy.hold}
                holdingLabel={copy.holding}
                onComplete={() => void finish(current)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </SignoffContext.Provider>
  );
}
